import { GetContract } from '@/services/fhir/contract/GetContract';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOrganizationContext } from './OrganizationContext';
import { Contract } from 'fhir/r5';

type ContractResource = Contract;

interface GuidanceAgentContextValue {
  contractGuidance: ContractResource | null;
  provider: 'openai' | 'gemini' | 'anthropic' | null;
  apiKey: string | null;
  refetchContract: () => Promise<void>;
}

interface GuidanceAgentContextProviderProps {
  children: React.ReactNode;
}

export const GuidanceAgentContext = createContext<GuidanceAgentContextValue>({} as GuidanceAgentContextValue);

export function GuidanceAgentContextProvider({ children }: GuidanceAgentContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractGuidance, setContractGuidance] = useState<ContractResource | null>(null);
  const [provider, setProvider] = useState<'openai' | 'gemini' | 'anthropic' | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const fetchContract = useCallback(async () => {
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractGuidance(null);
        setProvider(null);
        setApiKey(null);
        return;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "GUIDANCE_AGENT",
      });
      
      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry[0].resource;
        setContractGuidance(resource);

        const apiKeyExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/guidance_agent/api-key"
        );
        const providerExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/guidance_agent/provider"
        );

        if (apiKeyExtension?.valueString && providerExtension?.valueString) {
          setProvider(providerExtension.valueString as 'openai' | 'gemini' | 'anthropic');
          setApiKey(apiKeyExtension.valueString);
        } else {
          setProvider(null);
          setApiKey(null);
        }
      } else {
        setContractGuidance(null);
        setProvider(null);
        setApiKey(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato:", error);
      setContractGuidance(null);
      setProvider(null);
      setApiKey(null);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: GuidanceAgentContextValue = {
    contractGuidance,
    provider,
    apiKey,
    refetchContract: fetchContract,
  };

  return (
    <GuidanceAgentContext.Provider value={contextValue}>
      {children}
    </GuidanceAgentContext.Provider>
  );
} 

export function useGuidanceAgentContext() {
  return useContext(GuidanceAgentContext);
} 